/**
 * 作业指导书 SIP 编辑页（v0.50）。
 *
 * 需求（2026-10-14 用户原话）：
 *   「不同的制程SIP点进去单独出现编辑页面」   → 左侧五份文档，各一份编辑态
 *   「零件主数据里面手动输入的信息要跟后面 SIP 联动」 → 表头**只读**（实时读台账）
 *   「3、两个都要」= 零件主数据行 + 图纸行都放入口（`SipEntry` 两种形态）
 *
 * v0.55（2026-10-16 用户原话）：
 *   「1、增加右键功能」      → 左栏每一份都能右键（复制 / 新建空份 / 改名 / 删这份 / 导出这份）
 *   「2、比如制程不一定只有一份，任何实现多份」 → 唯一标识从 `doc_kind` 升级成
 *                              `doc_kind + seq`（这一份的那一份）
 *   「3、测量报告能不能放到自检下面」 → 撤掉 v0.54 的栏底钉法（见 styles.css）
 *
 * 三条不能破的边界（与后端 `services/sip_draft_service.py` 逐字对应）
 * -------------------------------------------------------------------
 * 1. **表头只读。** 取值唯一出处是零件主数据；本页一个输入框都不给 ——
 *    给的话，客户/材质这些字段就有了两个可写处（联动四原则之「权威唯一」）。
 * 2. **草稿只是覆盖层，不是唯一真相。** `source === 'default'` 时明确告诉用户
 *    「这 N 行是按工序规则现算的」；删掉草稿就回落，不会把文档弄丢。
 * 3. **导出用的是已保存的草稿，不是屏幕上的字。** 所以有未保存修改时**拦住**
 *    导出（见 `doExport`）—— 否则用户拿到一份少了他刚改那几行的受控文件，
 *    而屏幕上一切正常。**复制 / 新建空份同理**：它们读的也是库里的那一份。
 *
 * 多份（v0.55）的三条口径
 * -------------------------------------------------------------------
 * · **「第几份」由草稿行体现**：没有草稿就没有份。所以「复制 / 新建空份」都是
 *   先让后端落一行草稿，再切过去编辑 —— 界面侧的 `seq` 只是"在看哪一份"。
 * · **份名唯一出处是后端**（`display_name`）：界面不拼「制程 2」这种名字，
 *   起过名就显示用户的名字、没起名显示默认名。
 * · `MR`（测量报告）**固定一份**：右键只给「导出测量报告」，复制/新建由后端 400
 *   拒绝（界面连入口都不给，别让用户点了才发现不行）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBlankSipDraft, deleteSipDraft, duplicateSipDraft, exportSip,
  fetchBomRoutes, fetchRouteOps, fetchSipDocs, fetchSipPage,
  importRouteOps,
  listPartMasterDrawings, renameSipDraft, saveSipDraft,
  type RouteOp,
} from '../../api/client';
import type { SipDocStatus, SipEntry, SipHeaderField, SipItem,
              SipTreeLeaf } from '../../types';
import { loadBalloonStyle } from '../../lib/balloonStyle';
import { loadDocPos, saveDocPos } from '../../lib/navState';
import { useSipCols } from './sipColumns';
import { SipDrawingPicker } from './SipDrawingPicker';
import {
  SipItemsTable, newRow, rowFromItem, toRow, toWire, type SipRow,
} from './SipItemsTable';

type Toast = (m: string, t?: 'info' | 'success' | 'error') => void;

/** v0.54：第六份文档「测量报告」的 doc_kind（后端 `sip_draft_service.MR_KIND`）。
 *  ★ 它不是五份工序之一：没有 SIP 适配器、不走「导出 xlsx」——
 *    明细在这里编辑并存草稿，导出走「导出测量报告」弹窗（路由层读这份草稿）。
 *  ★ v0.55：它**固定一份**，右键只有「导出测量报告」（复制/新建后端 400 拒绝）。 */
const MR_KIND = 'MR';

/** 右键菜单的落点 —— 被右击的那一份。 */
interface Ctx {
  x: number;
  y: number;
  doc: SipDocStatus;
}

/** 左栏那一项上方的工序号该怎么写（唯一出处，别在 JSX 里又拼一遍）。
 *
 *  第一份：`-02`；第二份起：`-02 · 2` —— 让人一眼看出「还是那道工序，但换了份」，
 *  而 `-02` 这个工序号本身不跟着份号变（它标识的是工序，不是份）。 */
function railNo(d: SipDocStatus): string {
  if (d.is_mr) return 'MR';
  return d.seq > 0 ? `-${d.doc_kind} · ${d.seq + 1}` : `-${d.doc_kind}`;
}

/** 左栏那一项的第三行（状态）。
 *
 *  ★ 三态必须分开，否则「0」会被读成「这份文件是空的」：
 *    · 这一份不存在（没有草稿行）        → 「未存草稿」
 *    · 有草稿行、但明细为空（0 行）      → 「未编辑明细」
 *    · 有草稿行且明细非空                → 「已存 N 行」
 *    实测踩到过：-02 制程明明有 29 行，左栏却显示「0 行」。
 *
 *  🔴 判据必须是 `item_count === 0`，**不能**用 `edited`：
 *    `edited` 只区分「items_json 是不是 NULL」，而「新建空份」存的是 `[]`
 *    （**不是 NULL**）⇒ 后端按契约给 `edited=True, item_count=0`
 *    （见 `sip_draft_service.create_blank_draft` 的 docstring 与
 *      `backend/tests/test_sip_v055.py::test_blank_is_empty_and_flagged`）。
 *    拿 `edited` 当判据 ⇒ 空份会显示「已存 0 行」，正是要避免的那句话。
 *    两种情况（只改过名的 `edited=False`、存过空表的 `edited=True`）
 *    在界面上都该是「未编辑明细」—— 所以判 `item_count` 这一个数就够了。 */
function railStatus(d: SipDocStatus): string {
  if (!d.has_draft) return '未存草稿';
  return d.item_count > 0 ? `已存 ${d.item_count} 行` : '未编辑明细';
}

export function SipPage({ entry, canEdit, onToast, onBack, onOpenPart, onOpenMeasure, onPicked }: {
  entry: SipEntry;
  canEdit: boolean;
  onToast: Toast;
  /** 返回上一层（零件主数据 / 图纸工作台）。 */
  onBack: () => void;
  /** 跳到零件主数据并聚焦某一行。 */
  onOpenPart: (pmId: number) => void;
  /** v0.52：打开这张图纸的《测量报告》导出弹窗（附图页自动放气泡图）。
   *  弹窗实例在 App 层（与图纸工作台共用同一个），这里只报目标图纸 id。 */
  onOpenMeasure: (drawingId: number) => void;
  /** v0.52：pick 态选定图纸后把入口**升级**成 `drawing` 形态 ——
   *  App 层换掉 `sipEntry`（带 label），顶栏面包屑才能显示目标名；
   *  不升级的话顶栏只能停在无目标的「作业指导书」，选了哪张无从知晓。
   *  v0.90：从物料树直接点开某一份 SIP 时，额外带上 docKind/seq/routeOpId，
   *  让刷新后仍能记住打开的是哪一份。 */
  onPicked: (drawingId: number, label: string,
             docKind?: string, seq?: number, routeOpId?: number) => void;
}) {
  const qc = useQueryClient();
  const [drawingId, setDrawingId] = useState<number | null>(
    entry.kind === 'drawing' ? entry.id : null);
  /** 当前在看哪一份：`kind` = 哪道工序，`seq` = 那份工序的第几份（0 = 第一份）。
   *  ★ 两者**合起来**才定位一份文档 —— 只留 `kind` 的话，多份上线后
   *    「切到第 2 份制程」这件事根本表达不出来。 */
  const [kind, setKind] = useState<string | null>(null);
  const [seq, setSeq] = useState(0);
  /** v0.83：当前看的是**哪道工序的 SIP**（`0` = 常规五份 / 测量报告）。
   *  🔴 工序与工艺**只能来自 BOM 平台**（沈哥 2026-10-22）——
   *     本页只做「选工序 + 从 BOM 导入」，**不提供任何手工新增/编辑工序的入口**。 */
  const [routeOpId, setRouteOpId] = useState(0);
  /** 导入工序的弹层（null = 没开）。 */
  const [opPicker, setOpPicker] = useState(false);
  const [rows, setRows] = useState<SipRow[] | null>(null);
  const [remark, setRemark] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  /** v0.55：左栏右键菜单（null = 没开）。 */
  const [ctx, setCtx] = useState<Ctx | null>(null);
  /** v0.55：改名的弹层（null = 没开）。 */
  const [renaming, setRenaming] = useState<SipDocStatus | null>(null);

  // 换了一个入口（零件 A 的 SIP → 零件 B 的 SIP）时把整页状态清干净。
  // ⚠️ `entry` 是对象字面量，App 每次渲染都会造一个新的 ⇒ 依赖必须用
  //    `kind:id` 这个**字符串指纹**，不能用对象本身（否则每渲染一次就重置）。
  //    v0.52 的 `pick` 形态没有 id —— 指纹就是 `'pick'`（它不携带目标）。
  const entryId = entry.kind === 'pick' ? 0 : entry.id;
  // v0.90：`drawing` 形态带上 doc_kind/seq/route_op_id —— 同一图纸换一份 SIP 时
  //   指纹变化，重置 effect 才会重新预选到新那一份（否则只认第一次点的）。
  const entryKey = entry.kind === 'pick' ? 'pick'
    : entry.kind === 'drawing'
      ? `drawing:${entry.id}:${entry.docKind ?? ''}:${entry.seq ?? 0}:${entry.routeOpId ?? 0}`
      : `part:${entry.id}`;
  const entryLabel = entry.kind === 'pick' ? undefined : entry.label;
  useEffect(() => {
    setDrawingId(entry.kind === 'drawing' ? entry.id : null);
    // v0.90：从物料树直接点开某一份 SIP 时，entry 携带 docKind/seq/routeOpId
    //   ⇒ 预选到那一份（不再回落到左栏第一项）；同时跳过下面的「按图纸记位置」。
    if (entry.kind === 'drawing' && entry.docKind != null) {
      setKind(entry.docKind); setSeq(entry.seq ?? 0);
      setRouteOpId(entry.routeOpId ?? 0);
    } else {
      setKind(null); setSeq(0); setRouteOpId(0);
    }
    setRows(null); setRemark(''); setDirty(false);
    setCtx(null); setRenaming(null);
    // v0.83：换图纸 ⇒ 回到「常规五份」视角（不要停在上一个零件的某道工序上）
    setOpPicker(false);
  }, [entryKey, entry.kind, entryId]);

  // ------------------------------------------------- v0.65 记住「看到哪一份」
  // 用户 2026-10-17 报的是「刷新回到主数据」；页面保住之后紧接着会暴露第二个
  // 问题 —— **被弹回左栏第一项**（-01 原材料，本厂 6 张图全 0 行），
  // 看着还是"没记住我在哪"。所以位置一起记。
  //
  // ★ 按图纸分别记（键 `gdt_balloon_sip_pos_<drawing id>`）：换一张图纸回它
  //   自己的位置，而不是共用一个全局值被互相覆盖。
  // ⚠️ 这里**不校验**这份还在不在 —— 校验交给下面「(kind, seq) 必须存在于
  //    docs」那个 effect：份被删掉/份号前移时它会落到第一项，这里再判一遍
  //    就是两份实现。
  useEffect(() => {
    if (drawingId == null) return;
    // v0.90：从物料树带了明确的那一份 ⇒ 以它为准，不回退到上次记的位置
    if (entry.kind === 'drawing' && entry.docKind != null) return;
    const p = loadDocPos(drawingId);
    if (p) { setKind(p.kind); setSeq(p.seq); }
  }, [drawingId, entryKey]);

  // ⚠️ `kind == null` 时**什么都不做**：重置 effect 刚把它置空，这时写下去
  //    会把上一次的位置抹成「第一份」，刷新反而更糟。
  useEffect(() => {
    if (drawingId == null || kind == null) return;
    saveDocPos(drawingId, kind, seq);
  }, [drawingId, kind, seq]);

  // ---------------------------------------------------------------- 图纸解析
  // 从零件行进来时，台账**不绑定图纸**（0 张合法、多张常见）⇒ 这里自己解析：
  //   0 张  → 空态（告诉他去归户，不要给一个空白的编辑器）
  //   1 张  → 直接打开
  //   N 张  → 上面给一条「本零件名下的图纸」选择条
  const partDrawingsQ = useQuery({
    queryKey: ['part-drawings', entryId],
    queryFn: () => listPartMasterDrawings(entryId),
    enabled: entry.kind === 'part',
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    if (entry.kind !== 'part') return;
    const list = partDrawingsQ.data;
    if (!list || list.length !== 1) return;    // 0 张 / 多张：交给界面
    setDrawingId(list[0].id);
  }, [entry.kind, partDrawingsQ.data]);

  // ------------------------------------------------------ v0.52 顶栏入口（pick）
  // 从顶栏「作业指导书」进来时没有目标 ⇒ 给**全库图纸**选择列表。
  // 选完 `setDrawingId` 之后，本页与 `drawing` 入口完全同一条渲染路径 ——
  // 不另写第二套编辑区（两套实现迟早只改一处）。
  // v0.90：顶栏「作业指导书」入口的图纸清单改为「物料树」（`SipDrawingPicker`
  //   拉 `GET /api/sip/tree`），不再用 `listDrawings` 平铺全库图纸。

  // ---------------------------------------------------------------- 每份状态
  // ★ 工序号与中文名**不在这里写死** —— 唯一出处是后端（`sip_fill.DOC_KINDS`
  //   → `alloc.DOC_LABEL` → 接口返回的 `docs`）。前端再抄一份，改了后端就会
  //   「界面上少一份」，而那是最难发现的一种缺失。
  // ★ v0.55：`docs` 是**按份展开**的（每道工序至少一行 `seq=0` 占位 + 该工序
  //   已存在的第 2、3 份 + 末尾一行测量报告）⇒ 左栏直接按数组顺序渲染。
  const docsQ = useQuery({
    queryKey: ['sip-docs', drawingId],
    queryFn: () => fetchSipDocs(drawingId as number),
    enabled: drawingId != null,
    refetchOnWindowFocus: false,
  });
  /** 左栏要渲染的每一份（按后端顺序）。 */
  const docs = useMemo<SipDocStatus[]>(() => docsQ.data?.docs ?? [], [docsQ.data]);

  // ------------------------------------------------- v0.83 工序清单（来源 BOM）
  // ★ 这里**只读**：工序由 `importRouteOps` 从 `bom_route_ref` 导入，本平台不手工维护。
  const opsQ = useQuery({
    queryKey: ['route-ops', drawingId],
    queryFn: () => fetchRouteOps(drawingId as number),
    enabled: drawingId != null,
    refetchOnWindowFocus: false,
  });
  const ops = useMemo<RouteOp[]>(() => opsQ.data?.ops ?? [], [opsQ.data]);
  /** 当前选中的工序（`null` = 在看常规五份）。 */
  const curOp = useMemo(
    () => ops.find((o) => o.id === routeOpId) ?? null, [ops, routeOpId]);

  // 只有打开导入弹层时才查 BOM 路线（一料多路线，实测无法自动取舍 ⇒ 让人选）
  const bomRoutesQ = useQuery({
    queryKey: ['bom-routes', drawingId],
    queryFn: () => fetchBomRoutes(drawingId as number),
    enabled: drawingId != null && opPicker,
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    // ★ v0.83：看工序 SIP 时**必须跳过**这段校验 —— `docs` 是「常规五份」的清单，
    //   里面没有工序 SIP 的条目 ⇒ 不跳过的话，一打开工序 SIP 就被弹回「-01 原材料」，
    //   表现为「点了工序但页面没反应」。
    if (routeOpId !== 0) return;
    if (docs.length === 0) return;
    // ⚠️ 判据必须是**整对** `(kind, seq)`：只比 `doc_kind` 的话，删掉第 2 份
    //    之后界面会继续停在 `seq=1`（那一份已经不存在）⇒ 打开一个 404 的份。
    const ok = docs.some((d) => d.doc_kind === kind && d.seq === seq);
    if (!ok) { setKind(docs[0].doc_kind); setSeq(docs[0].seq); }
  }, [docs, kind, seq, routeOpId]);

  // ---------------------------------------------------------------- 当前那一份
  const pageQ = useQuery({
    // ★ v0.83：key 必须含 `routeOpId` —— 否则切工序时 RQ 认为"还是同一份"，
    //   直接把上一道工序的明细显示在新工序名下（最难查的那种错位）。
    queryKey: ['sip-page', drawingId, kind, seq, routeOpId],
    queryFn: () => fetchSipPage(drawingId as number, kind as string, seq,
                                routeOpId),
    enabled: drawingId != null && kind != null,
    // ⚠️ 必须关掉「聚焦就重取」：RQ 每次 fetch 都返回新对象，而下面那个同步
    //    effect 会在"这一份换人了"时把界面重置 ⇒ 切出去看一眼微信再回来，
    //    未保存的编辑就没了。
    refetchOnWindowFocus: false,
  });

  const lastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const d = pageQ.data;
    if (!d) return;
    // 「哪一份 + 这一次保存」的指纹：只有它变了才把界面重置成服务端的值。
    // ★ 必须含 `seq` —— 少了它，「切到同工序的第 2 份」指纹不变 ⇒ 界面继续
    //   显示第 1 份的行，而顶部标题已经变成第 2 份（最难查的那种错位）。
    // v0.83：指纹必须含 `route_op_id` —— 两道工序各自的第 1 份如果都 `seq=0`、
    //   又都在「未存草稿」状态，指纹就会相同 ⇒ 切工序后界面继续显示上一道的行。
    const k = `${d.drawing_id}|${d.doc_kind}|${d.seq}|${d.route_op_id ?? 0}`
      + `|${d.source}|${d.updated_at ?? ''}`;
    if (k === lastKeyRef.current) return;
    lastKeyRef.current = k;
    setRows(d.items.map(toRow));
    setRemark(d.remark ?? '');
    setDirty(false);
  }, [pageQ.data]);

  const data = pageQ.data;

  // ---------------------------------------------------------------- 编辑动作
  const patch = useCallback((i: number, p: Partial<SipRow>) => {
    setRows((cur) => {
      if (!cur) return cur;
      const next = cur.slice();
      const r = { ...next[i], ...p };
      // ⚠️ 界面里的 `is_dim` **只用来决定底色/提示**，它不是权威：
      //    保存时后端按 `nominal != null` 重新派生（服务层纪律 2）。
      //    所以这里的口径必须与后端一致 —— 「有标准值」⇔ nominal 非空。
      r.is_dim = r.nominal.trim() !== '';
      next[i] = r;
      return next;
    });
    setDirty(true);
  }, []);

  const move = useCallback((i: number, d: -1 | 1) => {
    setRows((cur) => {
      if (!cur) return cur;
      const j = i + d;
      if (j < 0 || j >= cur.length) return cur;
      const n = cur.slice();
      [n[i], n[j]] = [n[j], n[i]];
      return n;
    });
    setDirty(true);
  }, []);

  const remove = useCallback((i: number) => {
    setRows((cur) => (cur ? cur.filter((_, k) => k !== i) : cur));
    setDirty(true);
  }, []);

  const add = useCallback(() => {
    setRows((cur) => [...(cur ?? []), newRow()]);
    setDirty(true);
  }, []);

  const addFromAvailable = useCallback((it: SipItem) => {
    setRows((cur) => [...(cur ?? []), rowFromItem(it)]);
    setDirty(true);
  }, []);

  /** 切到「某道工序的第某份」。`dirty` 时先拦 —— 切过去未保存的编辑就没了。 */
  const switchDoc = useCallback((k: string, s: number) => {
    // v0.83：常规五份与工序 SIP 是**两个世界**（草稿各存各的）⇒ 切回来要清掉 routeOpId
    if (k === kind && s === seq && routeOpId === 0) return;
    if (dirty && !window.confirm(
      '当前这一份有未保存的修改，切过去就会丢掉。继续？')) return;
    setRouteOpId(0);
    setKind(k);
    setSeq(s);
  }, [dirty, kind, seq, routeOpId]);

  /** v0.83：切到**某道工序的 SIP**（固定 `doc_kind='02'` 制程 —— 决策 2
   *  「不新增导出模板」，复用制程模板）。`seq` 从这道工序的第 1 份开始。 */
  const switchOp = useCallback((op: RouteOp) => {
    if (routeOpId === op.id) return;
    if (dirty && !window.confirm(
      '当前这一份有未保存的修改，切过去就会丢掉。继续？')) return;
    setRouteOpId(op.id);
    setKind('02');
    setSeq(0);
  }, [dirty, routeOpId]);

  /** v0.83：从 BOM 导入工序。
   *  ⚠️ **会替换本图纸原有工序，并把挂在旧工序上的 SIP 明细一起清掉** ——
   *     这是后端 `import_from_bom(replace=True)` 的既定语义，必须先说清楚再确认。 */
  const doImportOps = useCallback(async (routeCode: string) => {
    if (drawingId == null) return;
    const had = ops.length;
    if (!window.confirm(had
      ? `导入会「替换」本图纸现有的 ${had} 道工序，\n`
        + '挂在旧工序上的 SIP 明细会一起清掉（不可撤销）。\n\n继续？'
      : `从 BOM 工艺路线 ${routeCode} 导入工序？`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await importRouteOps(drawingId, routeCode);
      onToast(`已导入 ${r.imported} 道工序`
        + (r.notes?.length ? `（${r.notes.length} 条提示，见控制台）` : ''),
              'success');
      if (r.notes?.length) console.info('[导入工序]', r.notes);
      setOpPicker(false);
      setRouteOpId(0);
      await qc.refetchQueries({ queryKey: ['route-ops', drawingId] });
    } catch (e: any) {
      onToast(`导入失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, ops.length, onToast, qc]);

  // ---------------------------------------------------------------- 右键菜单
  // 「点别处就关」，但**点菜单自己绝不能关**。
  // ⚠️ 只有 `setCtx(null)` + 菜单容器上的 `stopPropagation` 是**关不掉**的：
  //    window 上的监听注册在 **capture 阶段**（第 3 个参数 true），它跑在事件
  //    到达菜单容器之前，stopPropagation 根本来不及执行 ⇒ 用户按下左键的瞬间
  //    菜单就被卸载，`click` 永远落不到菜单项上（现象正是「右键出来的菜单点不动」）。
  //    正解：在关闭回调里判断事件目标是否落在菜单 DOM 内（`menuRef.contains`）。
  //    这段与 `DrawingTree.tsx` 是同一套 —— 那边有完整注释，这里不重复踩。
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ctx) return;
    const close = (e: Event) => {
      const t = e.target as Node | null;
      if (menuRef.current && t && menuRef.current.contains(t)) return;
      setCtx(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setCtx(null); };
    window.addEventListener('mousedown', close, true);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close, true);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctx]);

  const openMenu = useCallback((e: React.MouseEvent, d: SipDocStatus) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;              // v0.47：只读角色没有右键菜单（导出仍在页脚）
    // 贴近右/下边界时向内收，避免菜单被裁掉
    const W = 188, H = 210;
    setCtx({
      doc: d,
      x: Math.min(e.clientX, window.innerWidth - W - 8),
      y: Math.min(e.clientY, window.innerHeight - H - 8),
    });
  }, [canEdit]);

  // ---------------------------------------------------------------- 存 / 删 / 导出
  const doSave = useCallback(async () => {
    if (drawingId == null || kind == null || rows == null) return;
    setBusy(true);
    try {
      const r = await saveSipDraft(drawingId, kind, rows.map(toWire), remark,
                                   seq, routeOpId);
      setDirty(false);
      onToast(`草稿已保存：${r.saved} 行`, 'success');
      qc.invalidateQueries({ queryKey: ['sip-docs', drawingId] });
      // 工序 SIP 存完要刷「这道工序填了没有」的徽标（左栏工序区显示已存 N 项）
      qc.invalidateQueries({ queryKey: ['route-ops', drawingId] });
      qc.invalidateQueries({ queryKey: ['sip-page', drawingId] });
    } catch (e: any) {
      // 后端对「公差填了 `abc`」这类会回 400 并**指到第几行**，原样显示
      onToast(`保存失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, kind, seq, routeOpId, rows, remark, onToast, qc]);

  /** 删**这一份**：`seq=0` 是删草稿（回落到默认分配）；`seq>0` 是整份删掉
   *  （后端会把后面的份号前移补洞）。两种语义差别很大，文案必须分开写。 */
  const doReset = useCallback(async (doc: SipDocStatus) => {
    if (drawingId == null) return;
    const first = doc.seq === 0;
    const left = docs.filter((d) => d.doc_kind === doc.doc_kind).length - 1;
    if (dirty && !window.confirm(
      '当前这一份有未保存的修改，删除会一起丢掉。继续？')) return;
    if (!window.confirm(first
      ? `删除「${doc.name}」的草稿？\n\n下次打开会回落到按工序规则现算的初值；`
        + '屏幕上还没保存的修改也会一起丢。'
      : `删掉「${doc.name}」这一份？\n\n`
        + `明细与份名一起删（这道工序还剩 ${left} 份），后面的份自动前移补洞。`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await deleteSipDraft(drawingId, doc.doc_kind, doc.seq,
                                     routeOpId);
      onToast(first
        ? (r.deleted ? '草稿已删除，已回落到默认分配' : '这一份本来就没有草稿')
        : (r.deleted ? `已删除「${doc.name}」` : '这一份本来就不存在'),
              r.deleted ? 'success' : 'info');
      // ⚠️ 先等 `docs` 真的刷新完再动 `seq`：删第 2 份后必须回到 `seq-1`，
      //    而后端会把后面的份号**前移**（原第 3 份变成第 2 份）。不 await 的
      //    话，那个"有效性" effect 会拿旧 docs 判断新 seq ⇒ 界面跳回第 1 份
      //    （数据没丢，但用户以为删错了）。
      await qc.refetchQueries({ queryKey: ['sip-docs', drawingId] });
      qc.invalidateQueries({ queryKey: ['route-ops', drawingId] });
      qc.invalidateQueries({ queryKey: ['sip-page', drawingId] });
      if (!first) setSeq((s) => (s > 0 ? s - 1 : 0));
    } catch (e: any) {
      onToast(`删除失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, dirty, docs, routeOpId, onToast, qc]);

  /** 多份动作的公共前置：未保存就先存 —— 「复制 / 新建」读的是**库里**那一份。 */
  const guardUnsaved = useCallback((what: string): boolean => {
    if (!dirty) return true;
    onToast(`有未保存的修改 —— ${what}用的是已保存的草稿，请先「保存草稿」`, 'error');
    return false;
  }, [dirty, onToast]);

  /** 切到「刚造出来的那一份」：**必须先等 `docs` 刷完**再改 `seq`。
   *  否则那个"有效性" effect 会拿旧 docs 判断新 seq（新份还不在里面）
   *  ⇒ 刚复制/新建出来的份会被弹回第 1 份，用户以为没成功。 */
  const jumpToNew = useCallback(async (k: string, s: number) => {
    if (drawingId != null) {
      await qc.refetchQueries({ queryKey: ['sip-docs', drawingId] });
    }
    setKind(k);
    setSeq(s);
  }, [drawingId, qc]);

  /** 复制一份：源份没草稿时后端用**它的默认初值**当新份内容（不是空表）。 */
  const doDuplicate = useCallback(async (doc: SipDocStatus) => {
    if (drawingId == null) return;
    if (!guardUnsaved('复制')) return;
    setBusy(true);
    try {
      const r = await duplicateSipDraft(drawingId, doc.doc_kind, doc.seq);
      onToast(`已复制「${doc.name}」→「${r.name}」（${r.saved} 行`
        + `${r.source_of_new === 'default' ? '，源份是默认初值' : ''}）`, 'success');
      await jumpToNew(r.doc_kind, r.seq);
    } catch (e: any) {
      onToast(`复制失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, guardUnsaved, jumpToNew, onToast]);

  /** 新建空份（0 行）：空白是**合法且明确**的状态 —— 用户就是要从零手填。 */
  const doBlank = useCallback(async (doc: SipDocStatus) => {
    if (drawingId == null) return;
    if (!guardUnsaved('新建')) return;
    setBusy(true);
    try {
      const r = await createBlankSipDraft(drawingId, doc.doc_kind);
      onToast(`已新建「${r.name}」（空明细，从零手填）`, 'success');
      await jumpToNew(r.doc_kind, r.seq);
    } catch (e: any) {
      onToast(`新建失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, guardUnsaved, jumpToNew, onToast]);

  /** 改名（`title=""` = 恢复默认名）。名字只影响显示与导出文件名，不动明细。 */
  const doRename = useCallback(async (doc: SipDocStatus, title: string) => {
    if (drawingId == null) return;
    setBusy(true);
    try {
      const r = await renameSipDraft(drawingId, doc.doc_kind, doc.seq, title);
      onToast(title.trim() ? `已改名为「${r.name}」` : `已恢复默认名「${r.name}」`,
              'success');
      qc.invalidateQueries({ queryKey: ['sip-docs', drawingId] });
      qc.invalidateQueries({ queryKey: ['sip-page', drawingId, doc.doc_kind, doc.seq] });
    } catch (e: any) {
      onToast(`改名失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, onToast, qc]);

  const doExport = useCallback(async (doc: SipDocStatus) => {
    if (drawingId == null) return;
    // ★ 导出 = 「已保存的草稿」（无草稿则按工序规则现算），**不含屏幕上的字**。
    //   不拦的话，用户改完直接点导出，会拿到一份没有他那几行的受控文件。
    if (!guardUnsaved('导出')) return;
    // v0.54：测量报告的导出 = 打开《测量报告》弹窗（表头 12 字段在那里核对）。
    if (doc.is_mr) { onOpenMeasure(drawingId); return; }
    setBusy(true);
    try {
      // v0.69：气泡图是导出时现画的 ⇒ 必须把画布样式一起带上（引线开关等）
      // v0.77：样式按图纸存 ⇒ 取**本图纸**那一份，否则 SIP 图与画布不一致
      // v0.83：工序 SIP 导出必须带 routeOpId，否则导的是「常规制程」那一份
      const r = await exportSip(drawingId, doc.doc_kind, doc.seq,
                                loadBalloonStyle(drawingId), routeOpId);
      const n = r.itemCount >= 0 ? `，${r.itemCount} 行` : '';
      onToast(`已导出：${r.filename}（${r.fromDraft ? '用草稿' : '按默认分配'}${n}）`,
              'success');
    } catch (e: any) {
      onToast(`导出失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [drawingId, routeOpId, guardUnsaved, onOpenMeasure, onToast]);

  // ⚠️ 离开页面（返回 / 切走）时有未保存修改 —— 用 `beforeunload` 拦不住
  //    组件内的切换，所以这里在 `onBack` 上拦。
  const doBack = useCallback(() => {
    if (dirty && !window.confirm('当前有未保存的修改，返回就会丢掉。继续？')) return;
    onBack();
  }, [dirty, onBack]);

  // v0.54 的 `doExportMr` 已并入 `doExport(doc)`（v0.55）—— 测量报告走的还是
  // 打开导出弹窗那条路，只是入口从「页脚专用按钮」变成「按份统一入口 +
  // 右键『导出测量报告』」，两处调同一个函数，不会再出现两个实现。

  const partDrawings = partDrawingsQ.data ?? [];
  /** 明细表列显示（可见列 + 本机持久化）。提到页面级 ⇒ 切 tab 回来不丢。 */
  const cols = useSipCols();
  /** 当前看的那一份在 `docs` 里的状态（页脚按钮与右键菜单都用它 —— 唯一出处）。 */
  const currentDoc = useMemo<SipDocStatus | null>(() => {
    // v0.83：工序 SIP 不在 `docs` 里（那是常规五份的清单）⇒ 用一个合成对象顶上，
    //   否则页脚的「删除草稿 / 导出 xlsx」会因为 `currentDoc === null` 而永远禁用，
    //   表现为「工序 SIP 能编辑、但存不了也导不出」。
    if (routeOpId !== 0) {
      const d = pageQ.data;
      return {
        doc_kind: kind ?? '02',
        seq,
        label: '制程',          // 决策 2：工序 SIP 复用制程模板（doc_kind='02'）
        name: curOp ? `${curOp.op_name} · 工序 SIP` : '工序 SIP',
        title: '',
        is_mr: false,
        has_draft: d?.source === 'draft',
        item_count: d?.items?.length ?? 0,
        edited: d?.source === 'draft',
        updated_at: d?.updated_at ?? null,
        updated_by: d?.updated_by ?? '',
      } as unknown as SipDocStatus;
    }
    return docs.find((d) => d.doc_kind === kind && d.seq === seq) ?? null;
  }, [docs, kind, seq, routeOpId, curOp, pageQ.data]);

  // ---------------------------------------------------------------- 渲染
  // v0.52：顶栏入口（pick）还没选图纸时，左栏五份工序没有内容 ⇒ 整栏先不渲染，
  //        让选择列表占满（空栏只有标题会显得"页面坏了"）。
  const pickPending = entry.kind === 'pick' && drawingId == null;
  return (
    <div className="sip-page sip-layout">
      {/* ---------- 左栏：各道工序的每一份（**竖排**，不占垂直空间） ----------
          原来工序 tab 是横排一行（30px 高），改成左栏后：
          ① 每项能多显示「行数」，不必靠 tooltip；
          ② 省下的 30px 归给明细表（29 行时多露 1 行）；
          ③ 切换工序变成「换左栏」，与「换图纸」在视觉上分开。
          v0.55：一项 = **一份文档**（`doc_kind + seq`）而不是一道工序 ——
                 同一道工序的第 2、3 份各占一项，右键可复制/新建/改名/删。 */}
      {drawingId != null && (
      <nav className="sip-rail" aria-label="工序检验作业指导书">
        {/* v0.53.2：栏标题按用户口径改「工序检验作业指导书」。9 个字在
            116px 窄栏里 11px 会折行 ⇒ 10px 保单行（.sip-rail-title 同步改）。 */}
        <div className="sip-rail-title">工序检验作业指导书</div>

        {/* ===== v0.83：工序 SIP =====
            🔴 沈哥 2026-10-22：「工艺和工序，气泡图平台不能维护，只能来源 BOM 平台」
               ⇒ 这里**只列 + 只选**，没有任何"新增工序 / 改工序名"的入口。
                 没有工序时也不给手工兜底，而是直说「BOM 里没有工艺路线」。 */}
        <div className="sip-rail-title" style={{ opacity: 0.72, marginTop: 8 }}>
          工序 SIP（按工艺路线）
        </div>
        {opsQ.isLoading && (
          <div className="sip-rail-hint" style={{ padding: '2px 6px', lineHeight: 1.35 }}>
            正在读工序…
          </div>
        )}
        {!opsQ.isLoading && ops.length === 0 && (
          <div className="sip-rail-hint" style={{ padding: '2px 6px', lineHeight: 1.35 }}>
            BOM 里没有这个零件的工艺路线
          </div>
        )}
        {/* v0.84（沈哥 2026-10-22「制程要关联工艺路线」）：这张图纸已导入工艺路线时，
            把「工序」明确挂在「工艺路线」下面 —— 组标题显示路线 + 工序数，
            消除「工序和工艺路线两个独立列表」的观感。固定五份里的 -02 制程
            已由后端在存在 route_ops 时不返回，避免同一个制程出现两处。 */}
        {ops.length > 0 && (
          <div className="sip-rail-group"
               title={ops[0].route_code ? `工艺路线 ${ops[0].route_code}` : ''}>
            工艺路线 {ops[0].route_code || '—'} · {ops.length} 道工序
          </div>
        )}
        {ops.map((o) => (
          <button key={o.id}
                  className={`sip-rail-item${routeOpId === o.id ? ' on' : ''}`}
                  data-op-id={o.id}
                  title={[
                    `工序 ${o.op_no}：${o.op_name}`,
                    o.work_center ? `工作中心 ${o.work_center}` : '',
                    o.route_code ? `工艺路线 ${o.route_code}` : '',
                    (o.sip_item_count ?? 0) > 0
                      ? `已填 ${o.sip_item_count} 项检验` : '还没填 SIP',
                    '工序来源 BOM 平台，本平台只能选、不能改',
                  ].filter(Boolean).join('\n')}
                  onClick={() => switchOp(o)}>
            <span className="sip-rail-no">OP{o.op_no}</span>
            <span className="sip-rail-label">{o.op_name}</span>
            <span className="sip-rail-n">
              {(o.sip_item_count ?? 0) > 0 ? `已填${o.sip_item_count}项` : '未填'}
            </span>
          </button>
        ))}
        {canEdit && (
          <button className="btn sm" style={{ margin: '4px 6px 8px' }}
                  onClick={() => setOpPicker(true)}
                  title="工序来自 BOM 工艺路线（一料多路线时由你选一条）">
            {ops.length ? '重导工序' : '导入工序'}
          </button>
        )}

        {docs.map((d) => (
          <button key={`${d.doc_kind}:${d.seq}`}
                  className={`sip-rail-item${kind === d.doc_kind && seq === d.seq ? ' on' : ''}`
                    + `${d.seq > 0 ? ' sub' : ''}`}
                  data-doc-kind={d.doc_kind}
                  data-doc-seq={d.seq}
                  title={[d.has_draft
                    ? `已有草稿：${railStatus(d)}`
                      + (d.updated_by ? `（${d.updated_by}）` : '')
                      + (d.updated_at ? ` ${d.updated_at.slice(0, 19).replace('T', ' ')}` : '')
                    : '还没有草稿 —— 打开时按当前数据现算初值',
                    d.is_mr
                      ? '右键：导出测量报告'
                      : '右键：复制一份 / 新建空份 / 重命名 / 删除这一份 / 导出这一份',
                  ].join('\n')}
                  onClick={() => switchDoc(d.doc_kind, d.seq)}
                  onContextMenu={(e) => openMenu(e, d)}>
            <span className="sip-rail-no">{railNo(d)}</span>
            <span className="sip-rail-label">{d.name}</span>
            {/* 🔴 状态三态必须分开（见 `railStatus`）—— 直接显示「0 行」会让
                人以为「这份文件是空的」（实测踩到：-02 制程明明有 29 行）。
                v0.54：第六项 `MR`（测量报告）也走同一渲染 —— 后端 `docs`
                末尾带回，左栏位置在 -05 自检下面（styles.css 里已撤栏底钉法）。 */}
            <span className="sip-rail-n">{railStatus(d)}</span>
            {d.has_draft && <em className="sip-rail-badge">草稿</em>}
          </button>
        ))}
        {/* 右键是隐藏能力 —— 留一行提示，否则用户永远发现不了多份怎么建。 */}
        <div className="sip-rail-hint">右键一份：复制 / 新建 / 改名 / 导出</div>
      </nav>
      )}

      <div className="sip-main">
      <div className="sip-head">
        <button className="btn sm" onClick={doBack}
                title="返回上一层">‹ 返回</button>
        <div className="sip-head-title">
          <b>作业指导书 SIP</b>
          {/* v0.55：多份之后，「现在在看哪一份」必须写在页面上 ——
              左栏高亮的是一项序号（`-02 · 2`），但发截图/对照打印件时
              看的人需要一眼知道这是「制程 2」还是「制程（攻牙）」。 */}
          {data && <span className="sip-head-doc">{
            data.doc_count > 1 || data.title ? `${data.name}（共 ${data.doc_count} 份）` : data.name
          }</span>}
          <span className="sip-head-sub">
            {/* data 已加载时自带 `#id 文件名` ⇒ entryLabel（同文）不再重复拼。
                entryLabel 只在「还没读到数据」时兜底显示。 */}
            {!data && entryLabel ? `${entryLabel} · ` : ''}
            {data ? `#${data.drawing_id} ${data.filename}` :
              drawingId != null ? `#${drawingId}` : '（先选一张图纸）'}
          </span>
        </div>
        <span className="sip-flex" />
        {/* v0.53.1：测量报告入口统一收进**左栏底部**（`.sip-rail-mr`）——
            表头按钮与 -05 正文卡都撤了，全页只留这一个入口。 */}
        {data?.part_master_id != null && (
          <button className="btn sm"
                  title={`去零件主数据改表头（客户/材质/工艺流程…）—— 改完回来这里会自动跟着变。匹配口径：${data.part_match || '—'}`}
                  onClick={() => onOpenPart(data.part_master_id as number)}>
            在零件主数据里查看
          </button>
        )}
      </div>

      {/* ---------- v0.90 顶栏入口：物料树（客户 → 产品 → SIP） ----------
          叶子是 SIP，不是图纸：同产品多份 SIP 共用一份气泡图，图纸层不单列。
          点 SIP 直接打开那一份（`onPick` 带 doc_kind/seq/route_op_id）。 */}
      {pickPending && (
        <SipDrawingPicker
          currentKey={drawingId != null && kind != null
            ? `${drawingId}:${kind}:${seq}:${routeOpId}` : null}
          onToast={onToast}
          onPick={(sel: SipTreeLeaf) => {
            setDrawingId(sel.drawing_id);
            setKind(sel.doc_kind); setSeq(sel.seq); setRouteOpId(sel.route_op_id);
            // 入口升级成 drawing 形态（带上要打开的那一份）⇒ 顶栏面包屑显示目标名，
            // 且刷新后能记住打开的是哪一份。entryKey 变化会触发上面的重置 effect。
            onPicked(sel.drawing_id, sel.name,
                     sel.doc_kind, sel.seq, sel.route_op_id);
          }} />
      )}

      {/* ---------- 零件入口：本零件名下的图纸选择 ---------- */}
      {entry.kind === 'part' && partDrawingsQ.isLoading && (
        <div className="sip-state">正在查这个零件名下的图纸…</div>
      )}
      {/* ⚠️ 取数失败**必须**单独说 —— 否则 RQ 的行为是 `isLoading=false` +
          `data=undefined`，会直接掉进下面"这个零件名下还没有图纸"那个空态里，
          把「接口挂了」显示成「你还没归户图纸」。这是本项目「永远是 0 与已死掉
          同形」的老坑，两个分支必须分开。 */}
      {entry.kind === 'part' && partDrawingsQ.isError && (
        <div className="sip-state">
          <div className="sip-state-title">查不到这个零件名下的图纸</div>
          <div className="sip-state-desc">
            {(partDrawingsQ.error as any)?.message}
          </div>
        </div>
      )}
      {entry.kind === 'part' && !partDrawingsQ.isLoading
        && !partDrawingsQ.isError && partDrawings.length === 0 && (
        <div className="sip-state">
          <div className="sip-state-icon">◇</div>
          <div className="sip-state-title">这个零件名下还没有图纸</div>
          <div className="sip-state-desc">
            SIP 的检验明细来自<b>图纸上的气泡号与尺寸</b>，所以先要有一张归户到本零件的图纸。
            去图纸工作台把图纸归户过来，或换个零件。
          </div>
        </div>
      )}
      {entry.kind === 'part' && partDrawings.length > 1 && (
        <div className="sip-dwgbar">
          <span className="sip-dwgbar-label">
            这个零件名下有 {partDrawings.length} 张图纸 —— 选一张（SIP 按图纸出）：
          </span>
          {partDrawings.map((d) => (
            <button key={d.id}
                    className={`btn sm${drawingId === d.id ? ' primary' : ''}`}
                    title={`#${d.id} ${d.filename} · ${d.feature_count} 特征`}
                    onClick={() => {
                      if (drawingId === d.id) return;
                      if (dirty && !window.confirm(
                        '当前这一份有未保存的修改，换图纸会丢掉。继续？')) return;
                      setDrawingId(d.id);
                    }}>
              #{d.id} {d.filename}
            </button>
          ))}
        </div>
      )}

{/* ---------- 每份文档：状态提示（份选择已移到左栏 `.sip-rail`） ---------- */}
      {drawingId != null && docsQ.isLoading && (
        <div className="sip-state">正在读这一份的状态…</div>
      )}
      {drawingId != null && docsQ.isError && (
        <div className="sip-state">
          打不开这张图纸的 SIP：{(docsQ.error as any)?.message}
        </div>
      )}

      {/* ---------- 编辑区 ---------- */}
      {drawingId != null && kind != null && pageQ.isLoading && (
        <div className="sip-state">正在算这一份的检验明细…</div>
      )}
      {drawingId != null && kind != null && pageQ.isError && (
        <div className="sip-state">
          <div className="sip-state-title">打不开这一份</div>
          <div className="sip-state-desc">{(pageQ.error as any)?.message}</div>
        </div>
      )}
      {/* ⚠️ 判据必须带上 `seq` —— 只比 `doc_kind` 的话，切到同工序的第 2 份时
          `data` 还是第 1 份的（RQ 已换 key、新数据还没到），页面会把第 1 份的
          明细显示在「制程 2」的标题下面。 */}
      {data && kind === data.doc_kind && seq === data.seq && (
        <>
          {data.doc_kind === MR_KIND ? (
            /* v0.54：测量报告没有「零件主数据只读表头」—— 它的表头 12 字段
                在导出弹窗里核对填写（那里有自动抽取 + 出处徽章），这里只放
                说明与备注，别让两种表头挤一页。 */
            <MrHeadPanel remark={remark} readOnly={!canEdit}
                         onRemark={(v) => { setRemark(v); setDirty(true); }} />
          ) : (
            <SipHeaderPanel fields={data.header} empty={data.header_empty}
                            partMasterId={data.part_master_id}
                            onOpenPart={onOpenPart}
                            remark={remark} readOnly={!canEdit}
                            onRemark={(v) => { setRemark(v); setDirty(true); }} />
          )}

          {data.warnings.length > 0 && (
            <div className="sip-alerts warn">
              {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
          {/* v0.53.4（用户）：蓝色提示条（「还没有草稿…现算初值」/「用的是草稿…」）
              从每个作业指导书撤除 —— 状态在左栏「未存草稿 / 已存 N 行 / 草稿」
              徽标里已经有了，顶部再写一遍是重复。黄色 `.warn` 保留（真问题）。 */}

          <SipItemsTable rows={rows ?? []} readOnly={!canEdit}
                         available={data.available}
                         cols={cols}
                         onPatch={patch} onMove={move}
                         onRemove={remove}
                         onAdd={add}
                         onAddFromAvailable={addFromAvailable} />

          {/* 底部固定操作条：左=统计，右=动作。
              原来「备注」输入框 + 统计 + 三个按钮**全挤在一行**，
              高频的「保存草稿 / 导出 xlsx」被推到最右角落、还夹在备注框旁边。
              v0.55：动作作用的对象从「当前工序」变成「当前这一份」——
              按钮上的字必须跟着变（`currentDoc.seq > 0` 时「删除草稿」其实
              删的是整整一份，叫法不同是两种后果）。 */}
          <div className="sip-foot">
            <span className="sip-stats">
              {curOp ? `${curOp.op_name} · 工序 SIP` : data.name}
              {data.doc_count > 1 ? ` · 共 ${data.doc_count} 份` : ''}
              {' · '}{data.stats.n} 行 · 尺寸 {data.stats.dims} · 文本 {data.stats.specs}
              {data.stats.cc_sc > 0 ? ` · CC/SC ${data.stats.cc_sc}` : ''}
              {dirty ? ' · 有未保存修改' : ''}
            </span>
            <span className="sip-flex" />
            {canEdit ? (
              <>
                <button className="btn" disabled={busy || !currentDoc}
                        onClick={() => currentDoc && doReset(currentDoc)}
                        title={(currentDoc?.seq ?? 0) > 0
                          ? '删掉整整这一份（明细 + 份名），后面的份自动前移补洞'
                          : data.doc_kind === MR_KIND
                            ? '删掉草稿 ⇒ 下次打开回落到按识别特征现算的初值'
                            : '删掉草稿 ⇒ 下次打开回落到按工序规则现算的初值'}>
                  {(currentDoc?.seq ?? 0) > 0 ? '删除这一份' : '删除草稿'}
                </button>
                <button className="btn primary" disabled={busy || !dirty}
                        onClick={doSave}
                        title={dirty ? '把屏幕上的内容整体存成草稿' : '没有改动'}>
                  {busy ? '处理中…' : dirty ? '保存草稿' : '已保存'}
                </button>
              </>
            ) : (
              <span className="sip-readonly"
                    title="当前账号是只读角色 —— 可以看、可以导出，不能改草稿">
                只读账号：可看 / 可导出，不能改
              </span>
            )}
            {/* 页脚与右键菜单调**同一个** `doExport(doc)` —— 两处各写一份
                迟早只改一处（导出文件名带份号那套逻辑尤其经不起两份实现）。 */}
            {currentDoc && (
              <button className="btn" disabled={busy}
                      onClick={() => doExport(currentDoc)}
                      title={currentDoc.is_mr
                        ? '打开《测量报告》导出弹窗：核对表头 → 导出 xlsx（明细用这里保存的草稿；附图页自动放气泡图）'
                        : `生成「${data.name}」的 xlsx（用已保存的草稿；没有草稿就按工序规则现算）`}>
                {currentDoc.is_mr ? '导出测量报告' : '导出 xlsx'}
              </button>
            )}
          </div>
        </>
      )}
      </div>

      {/* ---------- v0.55 左栏右键菜单（与图纸树共用 `.ctx-menu` 样式） ---------- */}
      {ctx && (
        <DocContextMenu
          ctx={ctx}
          menuRef={menuRef}
          busy={busy}
          onClose={() => setCtx(null)}
          onOpen={() => {
            // 先关菜单再切 —— 否则菜单跟着"被点的那一份"留在屏幕上，
            // 而当前显示的是另一份（看着像点了没反应）。
            setCtx(null);
            switchDoc(ctx.doc.doc_kind, ctx.doc.seq);
          }}
          onDuplicate={() => { setCtx(null); void doDuplicate(ctx.doc); }}
          onBlank={() => { setCtx(null); void doBlank(ctx.doc); }}
          onRename={() => { setCtx(null); setRenaming(ctx.doc); }}
          onDelete={() => { setCtx(null); void doReset(ctx.doc); }}
          onExport={() => { setCtx(null); void doExport(ctx.doc); }}
        />
      )}

      {/* ---------- v0.55 重命名弹层 ---------- */}
      {renaming && (
        <RenameDialog
          doc={renaming}
          busy={busy}
          onCancel={() => setRenaming(null)}
          onSubmit={(t) => { const d = renaming; setRenaming(null); void doRename(d, t); }}
        />
      )}

      {/* v0.83：从 BOM 导入工序（唯一的工序来源） */}
      {opPicker && drawingId != null && (
        <OpImportDialog
          busy={busy}
          loading={bomRoutesQ.isLoading}
          error={(bomRoutesQ.error as any)?.message ?? null}
          materialCode={bomRoutesQ.data?.material_code ?? null}
          routes={bomRoutesQ.data?.routes ?? []}
          hasOps={ops.length > 0}
          onCancel={() => setOpPicker(false)}
          onPick={doImportOps}
        />
      )}
    </div>
  );
}

// ================================================================ v0.55 左栏右键
/** 一份文档的右键菜单。
 *
 *  · 五道工序：复制一份 / 新建空份 / 重命名 / 打开 / 导出这一份 / 删除这一份
 *  · 测量报告：**只给导出** —— 后端对「复制 / 新建 MR」是显式 400，
 *    界面连入口都不给，别让用户点了才发现不行。
 *  ★ 样式直接复用 `.ctx-menu` / `.ctx-item` / `.ctx-sep`（与图纸树同一份，
 *    见 styles.css）—— 再写一套 `.sip-ctx-*` 只会有两份会各自漂移的皮肤。 */
function DocContextMenu({
  ctx, menuRef, busy, onClose, onOpen, onDuplicate, onBlank, onRename, onDelete, onExport,
}: {
  ctx: Ctx;
  menuRef: React.RefObject<HTMLDivElement>;
  busy: boolean;
  onClose: () => void;
  onOpen: () => void;
  onDuplicate: () => void;
  onBlank: () => void;
  onRename: () => void;
  onDelete: () => void;
  onExport: () => void;
}) {
  const d = ctx.doc;
  const Item = ({ label, onClick, danger, hint }: any) => (
    <button className={`ctx-item${danger ? ' danger' : ''}`} disabled={busy}
            title={hint} onClick={(e) => { e.stopPropagation(); onClick(); }}>
      {label}
    </button>
  );
  // ⚠️ 这里的 `onMouseDown` stopPropagation **不是**「点菜单不关」的机制 ——
  //    window 上的 capture 监听跑在它之前，拦不住；真正的开关在父组件的
  //    `menuRef.contains(e.target)` 判定（同 DrawingTree 的注释）。
  return (
    <div className="ctx-menu" ref={menuRef} style={{ left: ctx.x, top: ctx.y }}
         onMouseDown={(e) => e.stopPropagation()}
         onContextMenu={(e) => e.preventDefault()}>
      <div className="ctx-head">{d.name}</div>
      {d.is_mr ? (
        <Item label="导出测量报告" onClick={onExport}
              hint="测量报告只有一份（它是一份导出报告，不是多道工序）" />
      ) : (
        <>
          <Item label="打开这一份" onClick={onOpen} />
          <div className="ctx-sep" />
          <Item label="复制一份" onClick={onDuplicate}
                hint="把这一份的明细照搬成新的一份（没存过草稿就用默认初值）" />
          <Item label="新建空份" onClick={onBlank}
                hint="0 行明细，从零手填" />
          <Item label="重命名" onClick={onRename}
                hint="改的是左栏显示名与导出文件名，不动明细" />
          <div className="ctx-sep" />
          <Item label="导出这一份 xlsx" onClick={onExport}
                hint="用已保存的草稿（没存过就按工序规则现算）；第 2 份起文件名带份号" />
          <Item label={d.seq > 0 ? '删除这一份' : '删除草稿（回落默认初值）'}
                onClick={onDelete} danger />
        </>
      )}
    </div>
  );
}

// ================================================================ v0.55 改名弹层
/** 给一份文档起名。留空 = 恢复默认名（「制程」/「制程 2」）。
 *
 *  ★ 默认名**不在前端拼** —— 提示语里的「留空恢复默认名」由后端 `display_name`
 *    决定叫什么，界面只说"恢复默认名"，不承诺具体字符串（后端改规则这里自动跟上）。 */
function RenameDialog({ doc, busy, onCancel, onSubmit }: {
  doc: SipDocStatus;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (title: string) => void;
}) {
  const [text, setText] = useState(doc.title || doc.name);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select(); }, 30);
  }, []);
  const ok = () => { if (!busy) onSubmit(text.trim()); };
  return (
    <>
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           onKeyDown={(e) => {
             if (e.key === 'Enter') ok();
             if (e.key === 'Escape') onCancel();
           }}>
        <div className="modal-head">
          <div className="modal-title">重命名这一份</div>
          <button className="btn sm" onClick={onCancel} title="关闭">✕</button>
        </div>
        <div className="modal-body">
          <input ref={inputRef} className="modal-input" value={text}
                 maxLength={64}
                 placeholder={`留空 = 用默认名（${doc.doc_kind === MR_KIND ? '测量报告' : doc.label}）`}
                 onChange={(e) => setText(e.target.value)} />
          <div className="modal-hint">
            名字会显示在左栏，并作为导出 xlsx 的文件名。最长 64 字符。
            {doc.seq > 0 ? ` 这是第 ${doc.seq + 1} 份。` : ''}
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onCancel}>取消</button>
          <button className="btn primary" onClick={ok} disabled={busy}>确定</button>
        </div>
      </div>
    </>
  );
}

/** v0.54：测量报告页的头部长条。
 *
 *  为什么不放表头 12 字段：《测量报告》的表头有自己的一套（自动抽取 / 文件名 /
 *  台账 / 上次 四种出处徽章），全在**导出弹窗**里核对填写（`MeasureReportDialog`）。
 *  这里若再摆一份就是「同一语义几份实现」—— 两处都能改同一个字段，改哪边
 *  以哪边为准必然说不清。这里只放「这份文档是什么 + 备注」。
 */
function MrHeadPanel({ remark, onRemark, readOnly }: {
  remark: string;
  onRemark: (v: string) => void;
  readOnly: boolean;
}) {
  return (
    <div className="sip-mr-head">
      <div className="sip-mr-head-line">
        <span className="sip-mr-title">测量报告</span>
        <span className="sip-mr-form">表单 XK240122062</span>
        <span className="sip-mr-note">
          明细在下面改、保存；「导出测量报告」用的就是这里保存的草稿
          （没存过就按识别特征现算）。表头在导出弹窗里核对填写。
        </span>
      </div>
      <label className="sip-remark">
        <span className="sip-remark-k">备注</span>
        <input type="text" value={remark} disabled={readOnly}
               data-field="remark"
               placeholder="存进草稿，供同事接手时看"
               onChange={(e) => onRemark(e.target.value)} />
      </label>
    </div>
  );
}

/** 表头（**只读**）—— 取值唯一出处是零件主数据。 */
function SipHeaderPanel({ fields, empty, partMasterId, onOpenPart,
                          remark, onRemark, readOnly }: {
  fields: SipHeaderField[];
  empty: boolean;
  partMasterId: number | null;
  onOpenPart: (pmId: number) => void;
  remark: string;
  onRemark: (v: string) => void;
  readOnly: boolean;
}) {
  const used = useMemo(() => fields.filter((f) => f.used), [fields]);
  const unused = useMemo(() => fields.filter((f) => !f.used), [fields]);
  /** 只有**有值**的字段进摘要行。
   *  🔴 实测：`-02制程` 的 9 个表头字段里 **5 个是 `—`**（客户/产品名称/内部编号/
   *     材料规格/材料材质）。把它们也摆出来 ⇒ 80px 高的一格里 5 个是横杠，
   *     而真正要看的「产品编号 / 图纸号」被挤在后面。 */
  const filled = useMemo(
    () => used.filter((f) => (f.value ?? '').trim() !== ''), [used]);
  const [open, setOpen] = useState(false);
  return (
    <div className="sip-hdr">
      <div className="sip-hdr-title">
        <span className="sip-hdr-title-t">表头</span>
        {empty ? (
          <span className="sip-hdr-note">
            这张图纸还没有归户到零件主数据 ⇒ 表头全空，导出的 xlsx 表头也是空的。
            请在图纸工作台把图纸归户到一个零件（或就地新建零件）。
          </span>
        ) : (
          <>
            {/* 摘要行：只列有值的，空的不占位 */}
            <span className="sip-hdr-sum">
              {filled.length === 0
                ? <em className="nil">本份表头字段全空（去零件主数据填）</em>
                : filled.map((f, i) => (
                    <span key={f.key} className="sip-hdr-sumitem">
                      {i > 0 && <i className="sep">·</i>}
                      <span className="k">{f.label}</span>
                      <span className="v">{f.value}</span>
                    </span>
                  ))}
            </span>
            <button className="pm-linkbtn" onClick={() => setOpen((v) => !v)}>
              {open ? '收起' : `全部 ${used.length} 项`}
            </button>
            <span className="sip-hdr-note">
              取值唯一出处是零件主数据 —— 要改请去那边，改完这里自动跟着变
            </span>
            {partMasterId != null && (
              <button className="pm-linkbtn" onClick={() => onOpenPart(partMasterId)}>
                去改 →
              </button>
            )}
          </>
        )}
      </div>
      {empty ? null : open && (
        <div className="sip-hdr-grid">
          {used.map((f) => (
            <div key={f.key} className="sip-hdr-cell">
              <span className="sip-hdr-k">{f.label}</span>
              <span className={`sip-hdr-v${f.value ? '' : ' nil'}`}>
                {f.value || '—'}
              </span>
            </div>
          ))}
        </div>
      )}
      {unused.length > 0 && open && (
        <div className="sip-hdr-unused"
             title="这些字段零件主数据里有值，但这一份文档的表头用不到它们">
          本份未用到：{unused.map((f) => f.label).join('、')}
        </div>
      )}

      {/* 备注搬到这里：它和表头一样是「这一份的元信息」，
          原来挤在页脚与保存/导出按钮同一行，把高频动作挤到了角落。 */}
      <label className="sip-remark">
        <span className="sip-remark-k">备注</span>
        <input type="text" value={remark} disabled={readOnly}
               data-field="remark"
               placeholder="存进草稿，供同事接手时看。例如：-05 自检只取 3 项，现场已确认"
               onChange={(e) => onRemark(e.target.value)} />
      </label>
    </div>
  );
}

// ================================================================ v0.83 工序导入
/** 从 BOM **导入**工序 —— 工序的唯一来源。
 *
 *  🔴 沈哥 2026-10-22：「工艺和工序，气泡图平台不能维护，只能来源 BOM 平台」
 *     ⇒ 这里**只有「选一条路线 → 导入」**，没有「新建工序 / 改工序名」。
 *     也不给"BOM 没有就手工加"的兜底 —— 那正是要避免的第二套真相。
 *
 *  ★ 为什么要选路线：一料多路线（实测 874 物料挂 915 条路线），而 `status`
 *    全 active、`erp_route_no` 全空、`updated_at` 同一天 —— 三个候选依据全部无效，
 *    无法自动取舍 ⇒ 由人在这里选，后端不猜。
 */
function OpImportDialog({ busy, loading, error, materialCode, routes,
                          hasOps, onCancel, onPick }: {
  busy: boolean;
  loading: boolean;
  error: string | null;
  /** `null` = 图纸还没归户（或归户的零件在 BOM 里查不到） */
  materialCode: string | null;
  routes: Array<{ route_code: string; route_name?: string | null; op_count: number }>;
  hasOps: boolean;
  onCancel: () => void;
  onPick: (routeCode: string) => void;
}) {
  return (
    <>
      <div className="modal-backdrop" onClick={onCancel} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           style={{ width: 'min(520px, 92vw)' }}>
        <div className="modal-title">从 BOM 导入工序</div>

        <div className="sip-state-desc" style={{ margin: '6px 0 10px' }}>
          工序与工艺由 <b>BOM 平台</b>维护，本平台<b>只能导入、不能新建或修改</b>。
        </div>

        {loading && <div className="sip-state">正在查 BOM 里的工艺路线…</div>}

        {!loading && error && (
          <div className="sip-state">
            <div className="sip-state-title">查不到 BOM 工艺路线</div>
            <div className="sip-state-desc">{error}</div>
          </div>
        )}

        {!loading && !error && materialCode == null && (
          <div className="sip-state">
            <div className="sip-state-title">这张图纸还没归户</div>
            <div className="sip-state-desc">
              工序挂在零件上 —— 先把图纸归户到 BOM 里的物料，再来导入。
            </div>
          </div>
        )}

        {!loading && !error && materialCode != null && routes.length === 0 && (
          <div className="sip-state">
            <div className="sip-state-title">
              BOM 里这个零件没有工艺路线
            </div>
            <div className="sip-state-desc">
              物料 <b>{materialCode}</b> 在 BOM 平台没有工艺路线，
              所以本平台出不了工序 SIP。<br />
              <b>请去 BOM 平台补工艺路线</b>，同步后回来重新导入
              （同步每天 03:10 自动跑，也可手动执行 <code>sync_bom.py</code>）。
            </div>
          </div>
        )}

        {!loading && !error && routes.length > 0 && (
          <div style={{ maxHeight: '46vh', overflow: 'auto', margin: '4px 0 10px' }}>
            {routes.map((r) => (
              <div key={r.route_code}
                   style={{
                     display: 'flex', alignItems: 'center', gap: 8,
                     padding: '6px 8px', borderBottom: '1px solid #eee',
                   }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{r.route_code}</div>
                  <div style={{ fontSize: 12, opacity: 0.72, whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.route_name || '（无名称）'} · {r.op_count} 道工序
                  </div>
                </div>
                <button className="btn sm primary" disabled={busy}
                        onClick={() => onPick(r.route_code)}>
                  导入
                </button>
              </div>
            ))}
          </div>
        )}

        {hasOps && routes.length > 0 && (
          <div className="sip-alerts warn" style={{ marginBottom: 8 }}>
            ⚠ 本图纸已有工序：导入会<b>替换</b>现有工序，挂在旧工序上的 SIP 明细会一起清掉。
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn" onClick={onCancel}>取消</button>
        </div>
      </div>
    </>
  );
}
